import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Modal from "../../../../../parts/modal-new";
import Copy from "../../../../../parts/inputs/copy";
import { ACTIVITY_MULTIPLIERS, calculateBmr, calculateTdee } from "../../../../../utils/nutrition-progress";
import { toDisplayWeight, toKg } from "./viewTokens";

// No height helper exists anywhere else in the repo (no schema field, no
// prior UI, and no prefill source — height always starts blank) — kept
// local rather than added to viewTokens.js, same "3rd occurrence to
// extract" rule applied throughout this feature.
const CM_PER_INCH = 2.54;
// feet/inches arrive as separate fields (matches the mobile app's macro
// calculator — a coach in Canada may want height in ft/in while still
// weighing in kg, so height/weight units are independent toggles here, not
// tied to each other or to the app-wide units setting). null when both
// are empty, so callers can tell "not entered" from "entered as 0".
const feetInchesToCm = (feet, inches) => {
  if (!feet && !inches) return null;
  const f = Number.parseFloat(feet) || 0;
  const i = Number.parseFloat(inches) || 0;
  return Math.round((f * 12 + i) * CM_PER_INCH * 10) / 10;
};

// Inverse — used to carry the entered value over (converted, not cleared)
// when the coach toggles height units mid-entry.
const cmToFeetInches = (cm) => {
  const totalInches = cm / CM_PER_INCH;
  let feet = Math.floor(totalInches / 12);
  let inches = Math.round((totalInches - feet * 12) * 10) / 10;
  if (inches >= 12) {
    feet += 1;
    inches -= 12;
  }
  return { feet, inches };
};

const cc = (t, key, opts) => t(`nutritionPage.progress.hero.calculatorModal.${key}`, opts);

// Age/Height/Weight are 3 otherwise-identical field+input pairs — only the
// id/label/value/setter differ.
const NumberField = ({ id, label, value, onChange }) => (
  <div className="np-tdee-calc-modal__field">
    <label htmlFor={id}>{label}</label>
    <input
      id={id}
      type="number"
      className="np-tdee-calc-modal__input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

NumberField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

// Small 2-way unit toggle (cm/ft, kg/lb) — shared by Height and Weight,
// each with its own independent unit state (see the component's own
// comment for the full "why").
const UnitToggle = ({ options, value, onChange }) => (
  <div className="np-tdee-calc-modal__unit-toggle" role="tablist">
    {options.map(({ key, label }) => (
      <button
        key={key}
        type="button"
        role="tab"
        aria-selected={value === key}
        className={`np-tdee-calc-modal__unit-btn${value === key ? " np-tdee-calc-modal__unit-btn--active" : ""}`}
        onClick={() => onChange(key)}
      >
        {label}
      </button>
    ))}
  </div>
);

UnitToggle.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.node })).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export const TdeeCalculatorModal = ({ isOpen, onClose, units, defaultGender, defaultWeightKg }) => {
  const { t } = useTranslation("athlete-portal");
  const isImperial = units === "imperial";

  const [gender, setGender] = useState("male");
  const [age, setAge] = useState("");
  // Height/weight units are independent toggles inside the modal (default
  // from the coach's app-wide units setting, but overridable) — NOT tied to
  // each other, matching the mobile app's macro calculator (a coach can
  // want height in ft/in while weighing in kg, or any other mix).
  const [heightUnit, setHeightUnit] = useState("cm");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [height, setHeight] = useState(""); // cm mode
  const [heightFeet, setHeightFeet] = useState(""); // ft mode
  const [heightInches, setHeightInches] = useState(""); // ft mode
  const [weight, setWeight] = useState("");
  const [activityKey, setActivityKey] = useState(null);

  // Carries the entered value over (converted, not cleared or silently
  // reinterpreted) when the coach toggles units mid-entry — the mobile app's
  // macro calculator does neither (it wipes height, and leaves weight's raw
  // digits untouched so they get reinterpreted as the new unit); this is a
  // deliberate improvement over that, not a mirror of it.
  const handleHeightUnitChange = (nextUnit) => {
    if (nextUnit === heightUnit) return;
    if (nextUnit === "ft") {
      const cm = Number.parseFloat(height);
      if (!Number.isNaN(cm) && cm > 0) {
        const { feet, inches } = cmToFeetInches(cm);
        setHeightFeet(String(feet));
        setHeightInches(String(inches));
      }
    } else {
      const cm = feetInchesToCm(heightFeet, heightInches);
      if (cm != null) setHeight(String(cm));
    }
    setHeightUnit(nextUnit);
  };

  const handleWeightUnitChange = (nextUnit) => {
    if (nextUnit === weightUnit) return;
    const parsed = Number.parseFloat(weight);
    if (!Number.isNaN(parsed) && parsed > 0) {
      const kgValue = toKg(parsed, weightUnit === "lb");
      setWeight(String(toDisplayWeight(kgValue, nextUnit === "lb")));
    }
    setWeightUnit(nextUnit);
  };

  // Reset every time the modal opens — same pattern as TdeeAdjustModal.js.
  // Age/height have no data source to prefill from (no schema field for
  // either anywhere in the repo); gender/weight prefill when available.
  useEffect(() => {
    if (!isOpen) return;
    setGender(defaultGender === "female" ? "female" : "male");
    setAge("");
    setHeightUnit(isImperial ? "ft" : "cm");
    setWeightUnit(isImperial ? "lb" : "kg");
    setHeight("");
    setHeightFeet("");
    setHeightInches("");
    setWeight(defaultWeightKg != null ? String(toDisplayWeight(defaultWeightKg, isImperial)) : "");
    setActivityKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Named intermediate values (each unit-resolved to the fixed unit the
  // engine expects) — the bmr/tdee useMemo below reads just these 2 plus
  // age/gender/activity, instead of every raw field individually.
  const heightCm = useMemo(
    () => (heightUnit === "cm" ? Number.parseFloat(height) : feetInchesToCm(heightFeet, heightInches)),
    [heightUnit, height, heightFeet, heightInches]
  );
  const weightKg = useMemo(() => {
    const parsed = Number.parseFloat(weight);
    return Number.isNaN(parsed) ? null : toKg(parsed, weightUnit === "lb");
  }, [weight, weightUnit]);

  const { bmr, tdee } = useMemo(() => {
    const parsedAge = Number.parseFloat(age);
    const inputsValid =
      !Number.isNaN(parsedAge) && parsedAge > 0 &&
      heightCm != null && !Number.isNaN(heightCm) && heightCm > 0 &&
      weightKg != null && weightKg > 0;
    if (!inputsValid) return { bmr: null, tdee: null };

    const computedBmr = calculateBmr({ gender, ageYears: parsedAge, heightCm, weightKg });
    const activityFactor = ACTIVITY_MULTIPLIERS.find((a) => a.key === activityKey)?.factor ?? null;
    const computedTdee = activityFactor != null ? calculateTdee(computedBmr, activityFactor) : null;
    return { bmr: computedBmr, tdee: computedTdee };
  }, [gender, age, heightCm, weightKg, activityKey]);

  if (!isOpen) return null;

  return (
    <Modal
      title={cc(t, "title")}
      className="np-tdee-calc-modal"
      onClose={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className="body np-tdee-calc-modal__body">
        <p className="np-tdee-calc-modal__disclaimer">
          {cc(t, "disclaimer")}
        </p>

        <NumberField id="np-tdee-calc-age" label={cc(t, "ageLabel")} value={age} onChange={setAge} />

        <div className="np-tdee-calc-modal__field">
          <span className="np-tdee-calc-modal__field-label np-tdee-calc-modal__field-label--gender">{cc(t, "genderLabel")}</span>
          <div className="np-tdee-calc-modal__mode-toggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={gender === "male"}
              className={`np-tdee-calc-modal__mode-btn${gender === "male" ? " np-tdee-calc-modal__mode-btn--active" : ""}`}
              onClick={() => setGender("male")}
            >
              {cc(t, "male")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={gender === "female"}
              className={`np-tdee-calc-modal__mode-btn${gender === "female" ? " np-tdee-calc-modal__mode-btn--active" : ""}`}
              onClick={() => setGender("female")}
            >
              {cc(t, "female")}
            </button>
          </div>
        </div>

        <div className="np-tdee-calc-modal__field">
          <div className="np-tdee-calc-modal__field-header">
            <label htmlFor="np-tdee-calc-height-cm">{cc(t, "heightLabel")}</label>
            <UnitToggle
              value={heightUnit}
              onChange={handleHeightUnitChange}
              options={[
                { key: "cm", label: cc(t, "unitCm") },
                { key: "ft", label: cc(t, "unitFt") },
              ]}
            />
          </div>
          {heightUnit === "cm" ? (
            <input
              id="np-tdee-calc-height-cm"
              type="number"
              className="np-tdee-calc-modal__input"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
            />
          ) : (
            <div className="np-tdee-calc-modal__feet-row">
              <input
                id="np-tdee-calc-height-ft"
                type="number"
                className="np-tdee-calc-modal__input"
                placeholder={cc(t, "feetPlaceholder")}
                value={heightFeet}
                onChange={(e) => setHeightFeet(e.target.value)}
              />
              <input
                id="np-tdee-calc-height-in"
                type="number"
                className="np-tdee-calc-modal__input"
                placeholder={cc(t, "inchesPlaceholder")}
                value={heightInches}
                onChange={(e) => setHeightInches(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="np-tdee-calc-modal__field">
          <div className="np-tdee-calc-modal__field-header">
            <label htmlFor="np-tdee-calc-weight">{cc(t, "weightLabel")}</label>
            <UnitToggle
              value={weightUnit}
              onChange={handleWeightUnitChange}
              options={[
                { key: "kg", label: cc(t, "unitKg") },
                { key: "lb", label: cc(t, "unitLb") },
              ]}
            />
          </div>
          <input
            id="np-tdee-calc-weight"
            type="number"
            className="np-tdee-calc-modal__input"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />
        </div>

        <div className="np-tdee-calc-modal__field">
          <span className="np-tdee-calc-modal__field-label">{cc(t, "activityLabel")}</span>
          <div className="np-tdee-calc-modal__activity-row">
            {ACTIVITY_MULTIPLIERS.map(({ key }) => (
              <button
                key={key}
                type="button"
                data-button-type={activityKey === key ? "primary" : "secondary"}
                onClick={() => setActivityKey(key)}
              >
                {cc(t, `activityLevels.${key}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="np-tdee-calc-modal__results">
          <div className="np-tdee-calc-modal__result-row">
            <span className="np-tdee-calc-modal__result-label">{cc(t, "bmrLabel")}</span>
            <span className="np-tdee-calc-modal__result-value">
              {bmr != null ? cc(t, "kcalDayValue", { value: bmr.toLocaleString() }) : "—"}
            </span>
          </div>
          <div className="np-tdee-calc-modal__result-row">
            <span className="np-tdee-calc-modal__result-label">{cc(t, "tdeeLabel")}</span>
            <span className="np-tdee-calc-modal__result-value">
              {tdee != null ? cc(t, "kcalDayValue", { value: tdee.toLocaleString() }) : "—"}
            </span>
            <Copy text={tdee != null ? String(tdee) : ""}>
              <button
                type="button"
                data-button-type="secondary"
                className="np-tdee-calc-modal__copy-btn"
                disabled={tdee == null}
              >
                {cc(t, "copyButton")}
              </button>
            </Copy>
          </div>
        </div>
      </div>
    </Modal>
  );
};

TdeeCalculatorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  units: PropTypes.string,
  defaultGender: PropTypes.string,
  defaultWeightKg: PropTypes.number,
};

export default TdeeCalculatorModal;
